// #region imports

// --- Angular
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

// --- RxJs
import { Subject, Subscription, combineLatest } from 'rxjs';

// --- HS Code
import { Constants, GroupModel, RespondentModel, RespondentStatuses } from '../../../models';

// #endregion

@Component({
  selector: 'hs-manage-respondents-group-v2',
  templateUrl: './manage-respondents-group-v2.component.html',
  styleUrls: ['./manage-respondents-group-v2.component.scss'],
  animations: [
    trigger('openClose', [
      transition(':enter', [
        style({height: '0px', overflow: 'hidden'}),
        animate('100ms', style({height: '*'}))
      ]),
      transition(':leave', [
        animate('10ms', style({height: '0px', overflow: 'hidden'}))
      ])
    ])
  ]
})
export class ManageRespondentsGroupV2Component implements OnChanges, OnDestroy {

  @Input() availableGroupsForMoves: GroupModel[] | null = null;
  @Input() group: GroupModel | null = null;
  @Input() groups: GroupModel[] | null = null;
  @Input() maximumNumberOfRespondentsInGroup: number = Constants.NUMBER_OF_RESPONDENTS_PER_GROUP;
  @Input() numberOfGroups: number = 0;
  @Input() isMobile: boolean | null = false;
  @Input() isMobileUser: boolean = false;

  @Output() addRespondent = new EventEmitter<{}>();
  @Output() deleteRespondent = new EventEmitter<RespondentModel>();
  @Output() editGroupName = new EventEmitter<GroupModel>();
  @Output() moveRespondent = new EventEmitter<{}>();
  @Output() remindRespondentClick = new EventEmitter<RespondentModel>();
  @Output() toggleVisibility = new EventEmitter<number>();

  completedRespondents: RespondentModel[] = [];
  isGroupFull: boolean = false;
  numberOfValidRespondents: number = 0;
  respondentSlots: number[] = [];
  setRespondents: RespondentModel[] = [];
  minimumNumberOfRespondentsInGroup: number = Constants.NUMBER_OF_RESPONDENTS_PER_GROUP;

  private _groupSubject$: Subject<GroupModel> = new Subject<GroupModel>();
  private _maximumNumberOfRespondentsInGroupSubject$: Subject<number> = new Subject<number>();
  private _respondentSlotsSubscription: Subscription = Subscription.EMPTY;

  constructor() {
    this._respondentSlotsSubscription = combineLatest({
      group: this._groupSubject$,
      upperLimit: this._maximumNumberOfRespondentsInGroupSubject$
    }).subscribe(({group, upperLimit}) => {
      if (!group || !upperLimit) return;

      this.setRespondents = group.respondents.filter((r: RespondentModel) => {
        return r.emailAddress.length > 0;
      });

      const numberOfRespondentSlotsToShow = Math.max(Math.max(Constants.NUMBER_OF_RESPONDENTS_PER_GROUP, this.setRespondents.length), upperLimit)
      this.respondentSlots = new Array<number>(numberOfRespondentSlotsToShow);

      for (let i = 0; i < numberOfRespondentSlotsToShow; i++) {
        this.respondentSlots[i] = i + 1;
      }

      this.isGroupFull = !!(this.setRespondents.length >= upperLimit);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['group'] && changes['group'].currentValue) {
      this.completedRespondents = this.group!.respondents.filter((r: RespondentModel) => r.status === RespondentStatuses.completedSurvey);
      this.numberOfValidRespondents = this.group!.respondents.filter(r => r.emailAddress.length > 0).length;

      this._groupSubject$.next(this.group!);
    }
    if (changes && changes['maximumNumberOfRespondentsInGroup']) {
      this._maximumNumberOfRespondentsInGroupSubject$.next(this.maximumNumberOfRespondentsInGroup);
    }
  }

  ngOnDestroy(): void {
    if (this._respondentSlotsSubscription !== Subscription.EMPTY) {
      this._respondentSlotsSubscription.unsubscribe();
      this._respondentSlotsSubscription = Subscription.EMPTY;
    }
  }

  onGroupNameEditClicked() {
    if (!this.group) return;

    this.editGroupName.emit(this.group);
  }

  toggleCollapse() {
    if (!this.group) return;
    if (this.group.isActiveGroup) return;

    this.group.isActiveGroup = true;
    this.toggleVisibility.emit(this.group.index);
  }

  onRespondentAdded(addRespondentParams: {}) {
    this.addRespondent.emit(addRespondentParams);
  }

  onRespondentDeleted(respondent: RespondentModel) {
    this.deleteRespondent.emit(respondent)
  }

  onRespondentMoved(moveRespondentParams: {}) {
    this.moveRespondent.emit(moveRespondentParams)
  }

}
